import {IOptionsSelectionsValue, IRenderingConfig, ModelContext} from '../../../types/app.types';
import {UrlModel} from './Url.model';
import {ImageModel} from './Image.model';
// eslint-disable-next-line import/no-deprecated
import {RenderingConfigModel} from '../RenderingConfig.model';
import {PriceModel} from '../Price.model';
import {DiscountModel} from './Discount.model';
import {DescriptionLineModel} from './DescriptionLine.model';
import {LineItemWithPrivateFields} from '../Cart/Cart.model';
import {DescriptionLine, PaymentOptionType as PaymentOptionTypeGQL} from '@wix/auto_sdk_ecom_current-cart';
import {ModifierGroupModel} from './ModifierGroup.model';
import {SPECS} from '../../specs';
import {SubscriptionInfoModel} from './SubscriptionInfo.model';
import {AttributesModel} from './Attributes.model';
import {PaymentConfigModel} from './PaymentConfig.model';
import {SourceModel} from './Source.model';
import {PricingModel} from './Pricing.model';
import {QuantityInfoModel} from './QuantityInfo.model';

export class LineItemModel {
  public id: string;
  public name: string;
  public attributes: AttributesModel;
  public paymentConfig: PaymentConfigModel;
  public quantityInfo: QuantityInfoModel;
  public source: SourceModel;
  public pricing: PricingModel;
  public modifierGroups: ModifierGroupModel[];
  /**
   * @deprecated
   */
  // eslint-disable-next-line import/no-deprecated
  public renderingConfig: RenderingConfigModel;
  public discounts: DiscountModel[];
  public fixedQuantity: boolean;

  constructor(params: LineItemModel) {
    this.id = params.id;
    this.name = params.name;
    this.attributes = params.attributes;
    this.paymentConfig = params.paymentConfig;
    this.quantityInfo = params.quantityInfo;
    this.source = params.source;
    this.pricing = params.pricing;
    this.modifierGroups = params.modifierGroups;
    // eslint-disable-next-line import/no-deprecated
    this.renderingConfig = params.renderingConfig;
    this.discounts = params.discounts;
    this.fixedQuantity = params.fixedQuantity;
  }

  public static fromCartV1(
    lineItem: LineItemWithPrivateFields,
    discountRulesNames: string[] = [],
    modelContext: ModelContext,
    renderingConfig?: IRenderingConfig,
    bookingsOverrideOptions?: IOptionsSelectionsValue[]
  ) {
    const crossedOutPrice = getCrossedOutPrice(lineItem);
    const modeledCrossedOutPrice = crossedOutPrice ? PriceModel.fromCartV1(crossedOutPrice, modelContext) : undefined;
    const modeledPrice = PriceModel.fromCartV1(lineItem?.price, modelContext);
    const modeledLineItemPrice = PriceModel.fromCartV1(lineItem.lineItemPrice, modelContext);
    const modeledModifiersTotalPrice = PriceModel.fromCartV1(lineItem.modifiersTotalPrice, modelContext);
    const quantity = lineItem.quantity;
    const itemType =
      lineItem.itemType?.preset?.toLowerCase() ?? /* istanbul ignore next */ lineItem.itemType?.custom?.toLowerCase();
    const url = UrlModel.fromCartV1(lineItem.url);
    const image = lineItem?.image ? ImageModel.fromCartV1(lineItem.image) : /* istanbul ignore next */ undefined;
    const descriptionLines = bookingsOverrideOptions
      ? (bookingsOverrideOptions ?? /* istanbul ignore next */ []).map((option: IOptionsSelectionsValue) =>
          DescriptionLineModel.fromGQL(option.title, option.value)
        )
      : (lineItem?.descriptionLines ?? /* istanbul ignore next */ []).map((descriptionLine: DescriptionLine) =>
          DescriptionLineModel.fromCartV1(descriptionLine)
        );
    const subscriptionInfo = modelContext.viewerContext.isExperimentEnabled(SPECS.SupportSubscriptionsInPurchaseFlow)
      ? SubscriptionInfoModel.fromCartV1(lineItem.subscriptionOptionInfo)
      : undefined;
    const depositAmount = lineItem?.depositAmount
      ? PriceModel.fromCartV1(lineItem.depositAmount, modelContext)
      : undefined;
    return new LineItemModel({
      // eslint-disable-next-line import/no-deprecated
      id: lineItem._id,
      name: lineItem.productName.translated,
      attributes: AttributesModel.fromCartV1({
        shippable: lineItem.physicalProperties?.shippable,
        itemType,
        url,
        image,
        descriptionLines,
        subscriptionInfo,
      }),
      paymentConfig: PaymentConfigModel.fromCartV1({
        savePaymentMethod: lineItem.savePaymentMethod,
        depositAmount,
        paymentOption: lineItem.paymentOption as PaymentOptionTypeGQL,
      }),
      quantityInfo: QuantityInfoModel.fromCartV1({
        quantity,
        availableQuantity: lineItem.availability?.quantityAvailable,
        fixedQuantity: lineItem.fixedQuantity,
      }),
      source: SourceModel.fromCartV1(lineItem?.catalogReference),
      pricing: PricingModel.fromCartV1({
        unitPrice: modeledPrice,
        totalPrice: modeledLineItemPrice,
        priceUndetermined: modelContext.viewerContext.isExperimentEnabled(
          SPECS.DontUseLineItemEnricherInCartAndCheckout
        )
          ? lineItem.priceUndetermined
          : false,
        comparePrice: modeledCrossedOutPrice,
        comparePriceByQuantity: crossedOutPrice
          ? PriceModel.fromCartV1(
              crossedOutPriceByQuantity({
                modeledCrossedOutPrice,
                quantity,
                modelContext,
              }),
              modelContext
            )
          : undefined,
        basePrice: PriceModel.fromCartV1(
          getBaseUnitPrice({
            modeledPrice,
            modeledCrossedOutPrice,
            modeledModifiersTotalPrice,
            modelContext,
          }),
          modelContext
        ),
        totalModifiersPrice: modeledModifiersTotalPrice,
      }),
      modifierGroups: (lineItem.modifierGroups ?? []).map((modifierGroup) =>
        ModifierGroupModel.fromCartV1(modifierGroup, modelContext)
      ),
      discounts: discountRulesNames.map((name) => DiscountModel.fromCartV1(name)),
      renderingConfig:
        !modelContext.viewerContext.isExperimentEnabled(SPECS.DontUseLineItemEnricherInCartAndCheckout) &&
        renderingConfig
          ? // eslint-disable-next-line import/no-deprecated
            RenderingConfigModel.fromGQL(renderingConfig)
          : undefined,
      fixedQuantity: modelContext.viewerContext.isExperimentEnabled(SPECS.DontUseLineItemEnricherInCartAndCheckout)
        ? lineItem.fixedQuantity
        : false,
    });
  }
}

function getCrossedOutPrice(lineItem: LineItemWithPrivateFields) {
  if (lineItem.priceBeforeDiscounts?.formattedConvertedAmount !== lineItem.price?.formattedConvertedAmount) {
    return lineItem.priceBeforeDiscounts;
  } else if (lineItem.fullPrice?.formattedConvertedAmount !== lineItem.price?.formattedConvertedAmount) {
    return lineItem.fullPrice;
  }
  return undefined;
}

function getBaseUnitPrice({
  modeledPrice,
  modeledCrossedOutPrice,
  modeledModifiersTotalPrice,
  modelContext,
}: {
  modeledPrice: PriceModel;
  modeledCrossedOutPrice: PriceModel;
  modeledModifiersTotalPrice?: PriceModel;
  modelContext: ModelContext;
}) {
  if (modeledModifiersTotalPrice.convertedAmount === 0) {
    return {
      convertedAmount: modeledPrice.convertedAmount.toString(),
      formattedConvertedAmount: modeledPrice.formattedConvertedAmount,
    };
  }

  const unitPriceAmount = modeledCrossedOutPrice?.convertedAmount
    ? modeledCrossedOutPrice?.convertedAmount
    : modeledPrice.convertedAmount;

  const baseUnitPriceAmount = unitPriceAmount - modeledModifiersTotalPrice.convertedAmount;

  return {
    convertedAmount: baseUnitPriceAmount.toString(),
    formattedConvertedAmount: modelContext.formatAmount(baseUnitPriceAmount),
  };
}

function crossedOutPriceByQuantity({
  modeledCrossedOutPrice,
  quantity,
  modelContext,
}: {
  modeledCrossedOutPrice: PriceModel;
  quantity: number;
  modelContext: ModelContext;
}) {
  return {
    convertedAmount: (modeledCrossedOutPrice.convertedAmount * quantity).toString(),
    formattedConvertedAmount: modelContext.formatAmount(modeledCrossedOutPrice.convertedAmount * quantity),
  };
}
